import { Navigate, Outlet, useLocation } from 'react-router'
import { ShieldAlert } from 'lucide-react'
import { useAuth } from '../../contexts/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { isAdminTierRole, isManagerTierRole } from '../../lib/roles'

interface ProtectedRouteProps {
  requiredRole?: 'Admin' | 'Manager' | 'Agent'
  /** Pannello Super Admin (cross-tenant): richiede isSuperAdmin, non solo il ruolo 'Admin' di agenzia. */
  requireSuperAdmin?: boolean
  /** Permesso granulare (es. 'Permissions.Agency.Settings'): mostra un messaggio di
   * accesso negato invece di reindirizzare, così chi arriva via URL diretto (non
   * dalla sidebar, che nasconde già la voce) capisce perché non può proseguire
   * invece di vedere un errore di caricamento generico dalla pagina stessa. */
  requiredPermission?: string
}

function AccessDenied({ message }: { message: string }) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-4 text-center">
      <ShieldAlert size={40} className="text-faint" />
      <h2 className="m-0 text-lg font-semibold text-heading">Accesso non consentito</h2>
      <p className="m-0 max-w-md text-sm text-muted">{message}</p>
    </div>
  )
}

export function ProtectedRoute({ requiredRole, requireSuperAdmin, requiredPermission }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { can } = usePermissions()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" role="status">
          <span className="sr-only">Caricamento...</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requireSuperAdmin && !user?.isSuperAdmin) {
    return <Navigate to="/" replace />
  }

  if (requiredRole === 'Admin' && !isAdminTierRole(user?.role)) {
    return <Navigate to="/" replace />
  }

  if (requiredRole === 'Manager' && !isManagerTierRole(user?.role)) {
    return <Navigate to="/" replace />
  }

  if (requiredPermission && !can(requiredPermission)) {
    return <AccessDenied message="Questa sezione è riservata al Titolare dell'agenzia. Se pensi sia un errore, contatta chi gestisce l'account." />
  }

  return <Outlet />
}
